import { APIProvider as GoogleMapsApiProvider } from '@vis.gl/react-google-maps';
import { type GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import AdobeTagManager from '@/dealership/AdobeTagManager';
import AutoLeadStarScript from '@/dealership/AutoLeadStarScript';
import DealerDotComScript from '@/dealership/DealerDotComScript';
import DealershipTracking from '@/dealership/DealershipTracking';
import FourEyesScript from '@/dealership/FourEyesScript';
import ShiftDigitalScript from '@/dealership/ShiftDigitalScript';
import DealershipPageEventWithVehicle from '@/vdp/dealership/DealershipPageEventWithVehicle';
import VehicleDetailsDealershipTracking from '@/vdp/dealership/DealershipTracking';
import HeadTags from '@/vdp/HeadTags';
import VehicleDetailsDealershipContainer from '@/vdp/VehicleDetailsContainer/VehicleDetailsDealershipContainer';
import {
  dealershipGoogleTagManagerId,
  googleMapsApiKey,
  shiftDigitalGoogleTagManagerId
} from '~/constants/environment';
import { ModalContextProvider } from '~/context/dealership/ModalContext';
import {
  type ContextType as StaticContextType,
  StaticContextProvider
} from '~/context/dealership/StaticContext';
import VehicleDetailsProviders, {
  type VehicleDetailsProvidersProps
} from '~/context/vdp/VehicleDetailsProviders';
import getDealershipProps from '~/server-props/common/getDealershipProps';
import { isNotFound, isRedirect } from '~/server-props/common/utils/guards';
import getVehicleDetailsServerSideProps from '~/server-props/vdp/getVehicleDetailsServerSideProps';
import { ErrorBoundary } from '~/utilities/ErrorBoundary';
import ErrorWithContext from '~/utilities/ErrorWithContext';

const TrackPageExit = dynamic(
  () =>
    import(
      /* webpackChunkName: "TrackPageExit" */ '@/vdp/Analytics/TrackPageExit'
    ),
  { ssr: false }
);

type OwnProps = {
  dealershipProps: StaticContextType | null;
  forProviders: VehicleDetailsProvidersProps;
  gtmId: string[];
};

export default function VehicleDetailsPage(props: OwnProps) {
  if (!props.dealershipProps) {
    throw new ErrorWithContext('No dealership props found', {
      location: 'Dealership vehicle page',
      ...props
    });
  }

  return (
    <ErrorBoundary section="Dealership VDP Page">
      <StaticContextProvider {...props.dealershipProps}>
        <GoogleMapsApiProvider apiKey={googleMapsApiKey}>
          <ModalContextProvider>
            <VehicleDetailsProviders {...props.forProviders}>
              <HeadTags />
              <AdobeTagManager />
              <AutoLeadStarScript />
              <DealerDotComScript />
              <FourEyesScript />
              <ShiftDigitalScript />
              <DealershipTracking
                trackAdobePageLoadEvent={false}
                trackShiftDigitalPageViewEvent={false}
              />
              <VehicleDetailsDealershipTracking />
              <DealershipPageEventWithVehicle />
              <TrackPageExit />
              <VehicleDetailsDealershipContainer />
            </VehicleDetailsProviders>
          </ModalContextProvider>
        </GoogleMapsApiProvider>
      </StaticContextProvider>
    </ErrorBoundary>
  );
}

export const getServerSideProps: GetServerSideProps<
  OwnProps
> = async context => {
  const result = await getVehicleDetailsServerSideProps<StaticContextType>({
    context,
    getOtherProps: requestContext => getDealershipProps(requestContext, 'vdp')
  });

  if (isNotFound(result) || isRedirect(result)) {
    return result;
  }

  const { otherProps, ...rest } = await result.props;
  const dealershipProps: StaticContextType | null = otherProps
    ? otherProps
    : null;

  return {
    props: {
      ...rest,
      dealershipProps,
      gtmId: [dealershipGoogleTagManagerId, shiftDigitalGoogleTagManagerId]
    }
  };
};
